import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Store } from "lucide-react";

export default function SellerApply() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [businessName, setBusinessName] = useState("");
  const [tagline, setTagline] = useState("");
  const [city, setCity] = useState("");
  const [about, setAbout] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [standType, setStandType] = useState("cake_pops");
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    if (user.is_seller) { nav("/seller/dashboard"); return; }
    api.get("/seller/me/application").then((r) => setExisting(r.data)).catch(() => {});
    // Prefill contact email with the user's account email as a helpful default
    if (user?.email && !contactEmail) setContactEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (about.trim().length < 20) { toast.error("Please write at least 20 chars about your shop"); return; }
    if (!contactEmail.trim()) { toast.error("Please enter a public contact email so buyers can reach you"); return; }
    setSubmitting(true);
    try {
      await api.post("/seller/apply", {
        business_name: businessName,
        tagline: tagline || null,
        city: city || null,
        about,
        contact_email: contactEmail.trim(),
        stand_type: standType,
      });
      toast.success("You're a seller — welcome! 🎉");
      await refresh();
      nav("/seller/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || user === false) return null;

  return (
    <>
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="flex items-center gap-3 mb-6">
          <Store className="w-6 h-6 text-[#FF7E67]" />
          <div>
            <div className="text-xs uppercase tracking-widest text-[#857163]">Marketplace</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#3B2C24]">Become a seller</h1>
          </div>
        </div>

        {existing?.status === "pending" ? (
          <div className="cp-card p-8 text-center" data-testid="pending-notice">
            <div className="font-heading text-2xl text-[#3B2C24] mb-2">Application in review 🌟</div>
            <p className="text-[#857163]">Your seller application was submitted. We'll notify you as soon as it's approved.</p>
          </div>
        ) : existing?.status === "rejected" ? (
          <div className="cp-card p-6">
            <p className="text-[#857163] mb-4">Your previous application wasn't approved. You can try again with more detail below.</p>
            <ApplyForm {...{ businessName, setBusinessName, tagline, setTagline, city, setCity, about, setAbout, contactEmail, setContactEmail, standType, setStandType, submit, submitting }} />
          </div>
        ) : (
          <div className="cp-card p-6">
            <p className="text-sm text-[#857163] mb-4">
              Tell us about your bakery. You'll get your own shop URL at <b>cakepoptalk.com/shop/{user.username}</b> and can list products with your own payment methods (CashApp, Venmo, PayPal, Zelle) — <b>instantly, no review required</b>.
            </p>
            <ApplyForm {...{ businessName, setBusinessName, tagline, setTagline, city, setCity, about, setAbout, contactEmail, setContactEmail, standType, setStandType, submit, submitting }} />
          </div>
        )}
      </main>
    </>
  );
}

function ApplyForm({ businessName, setBusinessName, tagline, setTagline, city, setCity, about, setAbout, contactEmail, setContactEmail, standType, setStandType, submit, submitting }) {
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="seller-apply-form">
      {/* Stand Type — first thing they pick so the rest of the form feels tailored */}
      <div>
        <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">
          Stand Type <span className="text-[#FF7E67]">*</span>
        </label>
        <div className="grid grid-cols-2 gap-2" data-testid="apply-stand-type">
          {[
            { key: "cake_pops", label: "Cake Pop Stand", accent: "#FF7E67", hint: "Cake pops, cakes & sweet treats" },
            { key: "farm_stand", label: "Farm Stand", accent: "#B91C1C", hint: "Produce, eggs, honey & preserves" },
          ].map((v) => {
            const active = standType === v.key;
            return (
              <button
                key={v.key}
                type="button"
                onClick={() => setStandType(v.key)}
                className="px-3 py-3 rounded-2xl text-left border-2 transition"
                style={active
                  ? { background: v.accent, color: "#fff", borderColor: v.accent }
                  : { background: "#fff", color: "#3B2C24", borderColor: "#E7DED5" }}
                data-testid={`apply-stand-type-${v.key}`}
              >
                <div className="font-semibold text-sm">{v.label}</div>
                <div className="text-[11px] mt-0.5" style={{ opacity: active ? 0.85 : 0.6 }}>{v.hint}</div>
              </button>
            );
          })}
        </div>
        <div className="text-xs text-[#857163] mt-1">This controls which pin type shows on the Locator map.</div>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Business name</label>
        <input className="cp-input" required minLength={2} maxLength={80} value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="apply-business-name" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">
          Public contact email <span className="text-[#FF7E67]">*</span>
        </label>
        <input
          type="email"
          required
          maxLength={120}
          className="cp-input"
          placeholder="orders@yourbakery.com"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          data-testid="apply-contact-email"
        />
        <div className="text-xs text-[#857163] mt-1">
          Shown on your product pages so buyers (including guests) can reach you.
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Tagline (optional)</label>
        <input className="cp-input" maxLength={140} placeholder="Custom cake pops for every celebration" value={tagline} onChange={(e) => setTagline(e.target.value)} data-testid="apply-tagline" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">City (optional)</label>
        <input className="cp-input" maxLength={80} placeholder="Boston, MA" value={city} onChange={(e) => setCity(e.target.value)} data-testid="apply-city" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">About your shop</label>
        <textarea className="cp-input min-h-[140px] resize-y" required minLength={20} maxLength={2000} placeholder="How long have you been baking cake pops? What makes yours special? Any specialties, flavors, or design styles?" value={about} onChange={(e) => setAbout(e.target.value)} data-testid="apply-about" />
      </div>
      <button type="submit" disabled={submitting} className="cp-btn-primary w-full disabled:opacity-70" data-testid="apply-submit">
        {submitting ? "Submitting…" : "Submit application"}
      </button>
    </form>
  );
}
